import { useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import { refreshAuthUser } from '@/lib/auth';
import { useAuthStore } from '@/stores/authStore';
import { resetDataClient } from '@/lib/amplify-client';
import { notifyCatalogRefresh } from '@/lib/catalog-refresh';
import { getPlatformSettings } from '@/api/platformSettings';
import { loadPlatformConfig } from '@/api/platformConfig';
import { ensureUserProfile, recordUserLogin } from '@/lib/recordUserLogin';
import { prefetchCategories } from '@/hooks/useCategories';

/** Hydrate auth session and load platform settings on boot. */
export function AuthBootstrap() {
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    prefetchCategories();
    void getPlatformSettings();
    void loadPlatformConfig();
  }, []);

  useEffect(() => {
    const hydrate = Promise.race([
      refreshAuthUser(),
      new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 10_000)),
    ]);
    void hydrate
      .then((user) => {
        setUser(user);
        if (user) void ensureUserProfile(user);
      })
      .finally(() => {
        useAuthStore.getState().setAuthHydrated(true);
      });

    const remove = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedOut') {
        resetDataClient();
        setUser(null);
        notifyCatalogRefresh();
      }
      if (payload.event === 'signedIn') {
        resetDataClient();
        void refreshAuthUser().then((user) => {
          if (user) {
            setUser(user);
            void ensureUserProfile(user).then(() => recordUserLogin(user));
            notifyCatalogRefresh();
          }
        });
      }
    });
    return remove;
  }, [setUser]);

  return null;
}
